import { useMemo } from "react";
import { CircularProgress, Button as MuiButton } from "@mui/material";
import { getButtonColors } from "./util";

export default function Button({
    loading,
    disabled,
    color = "primary",
    startIcon,
    variant = "soft",
    sx,
    textColor,
    bgColor,
    ...props
}) {
    const { bg, text, border, shadow } = useMemo(() => {
        return getButtonColors(color, variant, {
            text: textColor,
            bg: bgColor,
        });
    }, [color, variant, textColor, bgColor]);

    return (
        <MuiButton
            {...props}
            disabled={loading || disabled}
            startIcon={
                loading ? (
                    <CircularProgress size={20} sx={{ color: text }} />
                ) : (
                    startIcon
                )
            }
            sx={{
                backgroundColor: bg,
                color: text,
                border: `1px solid ${border}`,
                padding: "12px 18px",
                borderRadius: "3px",
                textTransform: "none",
                height: "44px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                letterSpacing: "0.3px",
                transition: "all 0.15s",
                fontWeight: "600",

                "&:hover": {
                    backgroundColor: variant === "text" ? shadow : bg,
                    boxShadow: `0 1px 6px ${shadow}`,
                },

                "&:active": {
                    transform: "translateY(1px)",
                },

                /* Our own colours are set above via sx, which outranks MUI's
                   disabled rule — so a disabled filled button would keep its
                   solid fill and only dim the label (unreadable). Paint the
                   whole disabled state from theme tokens instead, so it reads
                   the same in light and dark. */
                "&.Mui-disabled": {
                    backgroundColor:
                        variant === "contained" || variant === "soft"
                            ? "var(--bg-tint)"
                            : "transparent",
                    color: "var(--text-tertiary)",
                    borderColor:
                        variant === "text" ? "transparent" : "var(--border-mid)",
                    boxShadow: "none",
                },

                ...sx,
            }}
        />
    );
}
